// src/pages/MeetingReview.js
// Post-recording review form for consultants to fill in feedback before generating report
// Step-by-step guide through: Quotes → Section Scores → Topic Feedback → Participant Feedback

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore/lite';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { parseQuotes, formatDuration } from '../utils/shortcodes';
import { buildReportData, SECTION_TYPES } from '../utils/metrics';
import PageLoading from '../components/PageLoading';

const STEP_LABELS = {
  notes: 'Meeting Notes',
  quotes: 'Quotes',
  sections: 'Process Scores',
  topics: 'Topic Feedback',
  participants: 'Participant Feedback',
  review: 'Review & Publish',
};

// ─── Step Indicator ───────────────────────────────────────────────────────────

function StepIndicator({ currentStep, onStepClick }) {
  const steps = Object.entries(STEP_LABELS);
  return (
    <div className="step-indicator" style={{ marginBottom: 32 }}>
      {steps.map(([stepId, label], i) => {
        const isActive = currentStep === stepId;
        const isPast = steps.findIndex(s => s[0] === currentStep) > i;
        return (
          <button
            key={stepId}
            onClick={() => onStepClick(stepId)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px',
              borderRadius: 8,
              border: `1.5px solid ${isActive ? 'var(--navy)' : isPast ? 'var(--accent)' : 'var(--border)'}`,
              background: isActive ? 'var(--navy)' : isPast ? 'var(--accent)' : 'transparent',
              color: isActive || isPast ? 'white' : 'var(--ink-muted)',
              fontSize: '0.85rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'var(--transition)',
              fontFamily: 'var(--font-body)',
              flexShrink: 0,
            }}
          >
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 20, height: 20, borderRadius: '50%', fontSize: '0.72rem', fontWeight: 700,
              border: isActive ? '2px solid rgba(255,255,255,0.6)' : isPast ? '2px solid rgba(255,255,255,0.5)' : '2px solid var(--border)',
              background: isPast ? 'rgba(255,255,255,0.2)' : 'transparent',
              color: isActive || isPast ? 'white' : 'var(--ink-muted)',
              flexShrink: 0,
            }}>
              {isPast ? '✓' : ''}
            </span>
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ─── STEP 0: MEETING NOTES ───────────────────────────────────────────────────

const AVATAR_COLORS = [
  '#2a3d5b', '#5f7942', '#cba43a', '#922b3f',
  '#7209b7', '#4cc9f0', '#e76f51', '#4361ee',
];

function avatarColor(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function displayName(name) {
  return name?.split('@')[0] || name || 'Unknown';
}

function MeetingNotesStep({ entries, onEntriesChange }) {
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditText(entry.text || '');
  };

  const saveEdit = (entryId) => {
    onEntriesChange(entries.map(e =>
      e.id === entryId ? { ...e, text: editText } : e
    ));
    setEditingId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  if (entries.length === 0) {
    return (
      <div style={{ padding: '20px', background: 'var(--bg-soft)', borderRadius: 8, textAlign: 'center', color: 'var(--ink-muted)' }}>
        <p style={{ margin: 0, fontSize: '0.9rem' }}>No entries recorded in this meeting.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 8px 0', color: 'var(--navy)' }}>Edit Meeting Notes</h3>
        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--ink-soft)' }}>
          Review and correct any entries from the live recording. Click an entry to edit it.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {entries.map((entry, i) => {
          const color = avatarColor(entry.speakerName);
          const isEditing = editingId === entry.id;

          return (
            <div
              key={entry.id || i}
              style={{
                borderRadius: 8,
                border: `1.5px solid ${isEditing ? 'var(--navy)' : 'var(--border)'}`,
                background: isEditing ? 'var(--bg-soft)' : 'var(--bg-card)',
                overflow: 'hidden',
                transition: 'border-color 0.15s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: isEditing ? '1px solid var(--border)' : 'none' }}>
                <div style={{
                  width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                  background: color, color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.68rem', fontWeight: 700,
                }}>
                  {displayName(entry.speakerName)?.charAt(0)?.toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 700, fontSize: '0.82rem', color }}>
                    {displayName(entry.speakerName)}
                  </span>
                  {entry.section && (
                    <span style={{ fontSize: '0.68rem', color: '#4cc9f0', fontWeight: 600, marginLeft: 8 }}>§ {entry.section}</span>
                  )}
                  {entry.topic && (
                    <span style={{ fontSize: '0.68rem', color: '#f4a261', fontWeight: 600, marginLeft: 6 }}>#{entry.topic}</span>
                  )}
                  {entry.startTime && (
                    <span style={{ fontSize: '0.68rem', color: 'var(--ink-muted)', marginLeft: 8 }}>
                      {new Date(entry.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
                {!isEditing && (
                  <button
                    onClick={() => startEdit(entry)}
                    style={{
                      background: 'none', border: '1px solid var(--border)',
                      borderRadius: 6, padding: '4px 10px',
                      fontSize: '0.75rem', color: 'var(--ink-muted)',
                      cursor: 'pointer', flexShrink: 0,
                    }}
                  >
                    Edit
                  </button>
                )}
              </div>

              {isEditing ? (
                <div style={{ padding: '12px 14px' }}>
                  <textarea
                    autoFocus
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    rows={3}
                    style={{
                      width: '100%', padding: '10px 12px',
                      border: '1.5px solid var(--navy)', borderRadius: 8,
                      fontFamily: 'var(--font-body)', fontSize: '0.875rem',
                      color: 'var(--ink)', background: 'var(--bg-card)',
                      resize: 'vertical', outline: 'none', lineHeight: 1.6,
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                    <button onClick={cancelEdit} style={{ padding: '7px 16px', borderRadius: 6, border: '1.5px solid var(--border)', background: 'transparent', color: 'var(--ink-muted)', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Cancel</button>
                    <button onClick={() => saveEdit(entry.id)} style={{ padding: '7px 16px', borderRadius: 6, border: '1.5px solid var(--navy)', background: 'var(--navy)', color: 'white', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Save</button>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '8px 14px 12px', fontSize: '0.85rem', color: 'var(--ink-soft)', lineHeight: 1.6 }}>
                  {entry.text || <em style={{ color: 'var(--ink-muted)' }}>no text</em>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── STEP 1: QUOTES ───────────────────────────────────────────────────────────

function QuotesStep({ entries, selectedQuotes, onSelectionChange }) {
  const quotes = [];
  entries.forEach(entry => {
    const parsed = parseQuotes(entry.text || '');
    parsed.productive.forEach(text => {
      quotes.push({ id: `prod-${quotes.length}`, text, speaker: entry.speakerName, type: 'productive' });
    });
    parsed.unproductive.forEach(text => {
      quotes.push({ id: `unprod-${quotes.length}`, text, speaker: entry.speakerName, type: 'unproductive' });
    });
  });

  const toggleQuote = (quoteId) => {
    onSelectionChange({ ...selectedQuotes, [quoteId]: !selectedQuotes[quoteId] });
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 8px 0', color: 'var(--navy)' }}>Select Quotes to Share</h3>
        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--ink-soft)' }}>
          Any text wrapped in double quotes will be captured as a quote. Select the ones you want included in the report.
        </p>
      </div>

      {quotes.length === 0 ? (
        <div style={{ padding: '20px', background: 'var(--bg-soft)', borderRadius: 8, textAlign: 'center', color: 'var(--ink-muted)' }}>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>No quotes captured in this meeting.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {quotes.map(quote => (
            <label key={quote.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 16px', background: selectedQuotes[quote.id] ? (quote.type === 'productive' ? 'rgba(95,121,66,0.12)' : 'rgba(146,43,63,0.12)') : 'var(--bg-soft)', borderLeft: `3px solid ${quote.type === 'productive' ? '#5f7942' : '#922b3f'}`, borderRadius: 8, cursor: 'pointer', transition: 'var(--transition)' }}>
              <input type="checkbox" checked={selectedQuotes[quote.id] || false} onChange={() => toggleQuote(quote.id)} style={{ marginTop: 2, cursor: 'pointer', width: 18, height: 18 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.9rem', color: 'var(--ink)', marginBottom: 4, fontStyle: 'italic' }}>
                  {quote.type === 'productive' ? '"' : "'"}{quote.text}{quote.type === 'productive' ? '"' : "'"}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)' }}>— {quote.speaker?.split('@')[0] || 'Unknown'}</div>
              </div>
              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: quote.type === 'productive' ? '#5f7942' : '#922b3f', flexShrink: 0 }}>
                {quote.type === 'productive' ? '✅' : '❌'}
              </span>
            </label>
          ))}
        </div>
      )}

      <div style={{ marginTop: 24, padding: '12px 16px', background: 'rgba(95,121,66,0.08)', borderRadius: 8, fontSize: '0.85rem', color: 'var(--ink-soft)' }}>
        {Object.values(selectedQuotes).filter(Boolean).length} of {quotes.length} quotes selected
      </div>
    </div>
  );
}

// ─── STEP 2: SECTION SCORES ───────────────────────────────────────────────────

const SECTION_SUGGESTIONS = {
  lightning: ['Quick updates were crisp and well-prepared.', 'Some updates ran long and lost the room.', 'Team came prepared with relevant information.', 'Updates lacked context — more preparation needed.', 'Good energy and pace through the lightning round.'],
  clarity: ['Organizational clarity was clearly referenced and useful.', 'The team struggled to connect discussion to the clarity tools.', 'Purpose and values were invoked naturally in the conversation.', 'Roles and responsibilities were unclear during key decisions.', 'Strategic anchors helped keep the conversation focused.'],
  scoreboard: ['Numbers were current and the team engaged with them directly.', 'Scoreboard data was absent or out of date.', 'Metrics were discussed with appropriate context.', 'There was limited accountability language around the numbers.', 'The team showed clear ownership of their metrics.'],
  realtime: ['Issues were surfaced and resolved in real time.', 'Conversations drifted from the stated agenda items.', 'The team stayed focused on what mattered most.', 'Multiple topics competed for airtime simultaneously.', 'Strong use of parking for off-topic items.'],
  strategic: ['Strategic topics were given appropriate depth and time.', 'The discussion stayed tactical and missed the strategic opportunity.', 'Good debate — multiple perspectives were genuinely explored.', 'Decisions were made without sufficient analysis.', 'The team demonstrated clear strategic thinking.'],
  decisions: ['Decisions were named clearly and ownership was assigned.', 'Several decisions were implied but never explicitly confirmed.', 'The team committed to next steps with accountability.', 'It was unclear who owned what by the end of the discussion.', "Decisions reflected the team's strategic priorities."],
  cascading: ['Key messages were identified and assigned for communication.', 'No cascading messages were identified despite relevant decisions.', 'The team was clear about what needed to be shared externally.', 'Cascading messages lacked specificity on audience or timing.', 'Good awareness of what information needed to move downstream.'],
};

function SectionScoresStep({ sectionScores, sectionFeedback, onScoreChange, onFeedbackChange }) {
  const scoreLabels = ['Not Scored', 'Not Evident', 'Developing', 'Present', 'Strong', 'Exemplary'];
  const [usedSuggestions, setUsedSuggestions] = useState({});

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 8px 0', color: 'var(--navy)' }}>Rate Meeting Process</h3>
        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--ink-soft)' }}>
          Score how well the meeting adhered to the tactical meeting model (0–5 scale). Add brief feedback for each section.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {SECTION_TYPES.map(section => (
          <div key={section.id} className="card" style={{ padding: '20px 24px' }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>{section.label}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[0, 1, 2, 3, 4, 5].map(score => (
                  <button key={score} onClick={() => onScoreChange(section.id, score)} style={{ width: 40, height: 40, borderRadius: 6, border: `1.5px solid ${sectionScores[section.id] === score ? 'var(--navy)' : 'var(--border)'}`, background: sectionScores[section.id] === score ? 'var(--navy)' : 'transparent', color: sectionScores[section.id] === score ? 'white' : 'var(--ink-muted)', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', transition: 'var(--transition)', fontFamily: 'var(--font-body)' }}>
                    {score}
                  </button>
                ))}
              </div>
              {sectionScores[section.id] !== undefined && (
                <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', marginTop: 8 }}>
                  {scoreLabels[sectionScores[section.id]] || 'Not scored'}
                </div>
              )}
            </div>
            {SECTION_SUGGESTIONS[section.id] && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {SECTION_SUGGESTIONS[section.id].map((suggestion, si) => {
                  const key = `${section.id}-${si}`;
                  const used = !!usedSuggestions[key];
                  return (
                    <button key={si} disabled={used} onClick={() => { if (used) return; const current = sectionFeedback[section.id] || ''; const joined = current ? `${current.trimEnd()} ${suggestion}` : suggestion; onFeedbackChange(section.id, joined); setUsedSuggestions(prev => ({ ...prev, [key]: true })); }} style={{ padding: '4px 10px', borderRadius: 100, border: `1px solid ${used ? 'var(--accent)' : 'var(--border)'}`, background: used ? 'rgba(95,121,66,0.12)' : 'var(--bg-soft)', color: used ? 'var(--accent)' : 'var(--ink-muted)', fontSize: '0.75rem', cursor: used ? 'default' : 'pointer', fontFamily: 'var(--font-body)', transition: 'var(--transition)', textAlign: 'left', lineHeight: 1.4, opacity: used ? 0.7 : 1 }} onMouseEnter={e => { if (!used) { e.currentTarget.style.borderColor = 'var(--navy)'; e.currentTarget.style.color = 'var(--navy)'; }}} onMouseLeave={e => { if (!used) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--ink-muted)'; }}}>
                      {used ? '✓' : '+'} {suggestion}
                    </button>
                  );
                })}
              </div>
            )}
            <textarea placeholder={`Feedback for ${section.label}...`} value={sectionFeedback[section.id] || ''} onChange={e => onFeedbackChange(section.id, e.target.value)} rows={3} style={{ width: '100%', padding: '10px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontFamily: 'var(--font-body)', fontSize: '0.85rem', color: 'var(--ink)', background: 'var(--bg-soft)', resize: 'vertical', outline: 'none' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── STEP 3: TOPIC FEEDBACK ───────────────────────────────────────────────────

function TopicFeedbackStep({ topics, topicFeedback, topicDecisions, onFeedbackChange, onDecisionChange }) {
  if (topics.length === 0) {
    return (
      <div style={{ padding: '20px', background: 'var(--bg-soft)', borderRadius: 8, textAlign: 'center', color: 'var(--ink-muted)' }}>
        <p style={{ margin: 0, fontSize: '0.9rem' }}>No topics were tagged in this meeting.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 8px 0', color: 'var(--navy)' }}>Topic Analysis</h3>
        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--ink-soft)' }}>
          For each topic, mark the decision status and provide feedback on the discussion.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {topics.map((topic, i) => (
          <div key={i} className="card" style={{ padding: '20px 24px' }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>{topic.label}</div>
              <div style={{ display: 'flex', gap: 14, fontSize: '0.78rem', color: 'var(--ink-muted)', marginBottom: 16, flexWrap: 'wrap' }}>
                <span>⏱ {formatDuration(topic.ms)}</span>
                <span>👥 {topic.speakers} speaker{topic.speakers !== 1 ? 's' : ''}</span>
                <span>❓ {topic.questions} question{topic.questions !== 1 ? 's' : ''}</span>
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>Decision Status</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['Decided', 'Tabled', 'Not Ready', 'Ongoing'].map(status => (
                  <button key={status} onClick={() => onDecisionChange(i, status)} style={{ padding: '6px 14px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', border: `1.5px solid ${topicDecisions[i] === status ? 'var(--navy)' : 'var(--border)'}`, background: topicDecisions[i] === status ? 'var(--navy)' : 'transparent', color: topicDecisions[i] === status ? 'white' : 'var(--ink-muted)', transition: 'var(--transition)', fontFamily: 'var(--font-body)' }}>{status}</button>
                ))}
              </div>
            </div>
            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--ink)', display: 'block', marginBottom: 8 }}>Feedback & Observations</label>
              <textarea placeholder="What went well? What could improve?" value={topicFeedback[i] || ''} onChange={e => onFeedbackChange(i, e.target.value)} rows={3} style={{ width: '100%', padding: '10px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontFamily: 'var(--font-body)', fontSize: '0.85rem', color: 'var(--ink)', background: 'var(--bg-soft)', resize: 'vertical', outline: 'none' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── STEP 4: PARTICIPANT FEEDBACK ─────────────────────────────────────────────

function ParticipantFeedbackStep({ participants, participantFeedback, onFeedbackChange }) {
  if (participants.length === 0) {
    return (
      <div style={{ padding: '20px', background: 'var(--bg-soft)', borderRadius: 8, textAlign: 'center', color: 'var(--ink-muted)' }}>
        <p style={{ margin: 0, fontSize: '0.9rem' }}>No participants recorded.</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 8px 0', color: 'var(--navy)' }}>Participant Observations</h3>
        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--ink-soft)' }}>
          Provide specific feedback for each participant. Note their strengths, contributions, and areas to develop.
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {participants.map((participant, i) => (
          <div key={i} className="card" style={{ padding: '16px 20px', borderLeft: '3px solid var(--olive)' }}>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--navy)', marginBottom: 12 }}>{participant.name.split('@')[0]}</div>
            <textarea placeholder={`Observations for ${participant.name.split('@')[0]}...`} value={participantFeedback[participant.name] || ''} onChange={e => onFeedbackChange(participant.name, e.target.value)} rows={3} style={{ width: '100%', padding: '10px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontFamily: 'var(--font-body)', fontSize: '0.85rem', color: 'var(--ink)', background: 'var(--bg-soft)', resize: 'vertical', outline: 'none' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── STEP 5: REVIEW & PUBLISH ─────────────────────────────────────────────────

function ReviewStep({ meeting, formData, onPublish, isSaving }) {
  const selectedQuoteCount = Object.values(formData.selectedQuotes).filter(Boolean).length;
  const sectionScoreCount = Object.values(formData.sectionScores).filter(v => v !== undefined).length;
  const topicFeedbackCount = Object.values(formData.topicFeedback).filter(v => typeof v === 'string' ? v.trim() : v != null).length;
  const participantFeedbackCount = Object.values(formData.participantFeedback).filter(v => v?.trim()).length;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 8px 0', color: 'var(--navy)' }}>Review & Publish</h3>
        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--ink-soft)' }}>
          Verify your feedback before publishing the report. The team leader will be able to see the full report.
        </p>
      </div>
      <div className="grid-2" style={{ marginBottom: 24, gap: 12 }}>
        <div className="stat-card"><div className="stat-label">Quotes Selected</div><div className="stat-value">{selectedQuoteCount}</div><div className="stat-sub">for shareable report</div></div>
        <div className="stat-card"><div className="stat-label">Section Scores</div><div className="stat-value">{sectionScoreCount}/{SECTION_TYPES.length}</div><div className="stat-sub">process areas rated</div></div>
        <div className="stat-card"><div className="stat-label">Topic Feedback</div><div className="stat-value">{topicFeedbackCount}</div><div className="stat-sub">topics with feedback</div></div>
        <div className="stat-card"><div className="stat-label">Participant Notes</div><div className="stat-value">{participantFeedbackCount}</div><div className="stat-sub">team members noted</div></div>
      </div>
      <div style={{ padding: '16px', background: 'rgba(95,121,66,0.08)', borderRadius: 8, borderLeft: '3px solid var(--accent)', marginBottom: 24 }}>
        <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--ink-soft)' }}>
          ✓ Once you publish, the team leader will receive the full report with your feedback. You can always come back to edit individual sections.
        </p>
      </div>
      <button onClick={onPublish} disabled={isSaving} style={{ width: '100%', padding: '14px 20px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: 'white', fontSize: '0.95rem', fontWeight: 700, cursor: isSaving ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.6 : 1, transition: 'var(--transition)', fontFamily: 'var(--font-body)' }}>
        {isSaving ? '⏳ Publishing...' : '✓ Publish Report'}
      </button>
    </div>
  );
}

// ─── MAIN REVIEW PAGE ─────────────────────────────────────────────────────────

export default function MeetingReview() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { userProfile, currentUser } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState('notes');

  const [formData, setFormData] = useState({
    selectedQuotes: {},
    sectionScores: {},
    sectionFeedback: {},
    topicFeedback: {},
    topicDecisions: {},
    participantFeedback: {},
  });
  const [editedEntries, setEditedEntries] = useState([]);

  useEffect(() => { loadMeeting(); }, [meetingId]);

  async function loadMeeting() {
    try {
      const snap = await getDoc(doc(db, 'meetings', meetingId));
      if (snap.exists()) {
        const data = snap.data();
        setMeeting({ id: snap.id, ...data });
        setFormData({
          selectedQuotes: data.selectedQuotes || {},
          sectionScores: data.sectionScores || {},
          sectionFeedback: data.sectionFeedback || {},
          topicFeedback: data.topicFeedback || {},
          topicDecisions: data.topicDecisions || {},
          participantFeedback: data.participantFeedback || {},
        });
        setEditedEntries(data.entries || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function publishReport() {
    if (!meeting) return;
    setIsSaving(true);
    try {
      await updateDoc(doc(db, 'meetings', meetingId), {
        entries: editedEntries,
        selectedQuotes: formData.selectedQuotes,
        sectionScores: formData.sectionScores,
        sectionFeedback: formData.sectionFeedback,
        topicFeedback: formData.topicFeedback,
        topicDecisions: formData.topicDecisions,
        participantFeedback: formData.participantFeedback,
        status: 'reviewed',
        reviewedAt: serverTimestamp(),
        reviewedBy: currentUser?.uid,
        lastUpdatedAt: serverTimestamp(),
      });
      navigate(`/meetings/${meetingId}/report`);
    } catch (err) {
      console.error(err);
      alert('Error publishing report. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  if (loading) return <PageLoading text="Loading meeting..." />;
  if (!meeting) return <p style={{ color: 'var(--ink-muted)', padding: 48 }}>Meeting not found.</p>;

  const entries = editedEntries.length > 0 ? editedEntries : (meeting.entries || []);
  const reportData = buildReportData(entries, {});
  const participants = meeting.attendees || [];

  return (
    <div className="fade-up">
      <div style={{ marginBottom: 24 }}>
        <button onClick={() => navigate(`/meetings/${meeting.teamId}`)} className="link" style={{ fontSize: '0.85rem' }}>
          ← Back to team
        </button>
      </div>

      <div className="report-header">
        <div className="report-header-meta">Meeting Review</div>
        <h2>{meeting.name}</h2>
        <div className="report-header-sub">{meeting.teamName} · {entries.length} entries recorded</div>
      </div>

      <div className="card">
        <StepIndicator currentStep={currentStep} onStepClick={setCurrentStep} />

        {currentStep === 'notes' && <MeetingNotesStep entries={editedEntries} onEntriesChange={setEditedEntries} />}
        {currentStep === 'quotes' && <QuotesStep entries={entries} selectedQuotes={formData.selectedQuotes} onSelectionChange={newQuotes => setFormData(prev => ({ ...prev, selectedQuotes: newQuotes }))} />}
        {currentStep === 'sections' && (
          <SectionScoresStep
            sectionScores={formData.sectionScores}
            sectionFeedback={formData.sectionFeedback}
            onScoreChange={(id, score) => setFormData(prev => ({ ...prev, sectionScores: { ...prev.sectionScores, [id]: score } }))}
            onFeedbackChange={(id, fb) => setFormData(prev => ({ ...prev, sectionFeedback: { ...prev.sectionFeedback, [id]: fb } }))}
          />
        )}
        {currentStep === 'topics' && (
          <TopicFeedbackStep
            topics={reportData.topics}
            topicFeedback={formData.topicFeedback}
            topicDecisions={formData.topicDecisions}
            onFeedbackChange={(i, fb) => setFormData(prev => ({ ...prev, topicFeedback: { ...prev.topicFeedback, [i]: fb } }))}
            onDecisionChange={(i, dec) => setFormData(prev => ({ ...prev, topicDecisions: { ...prev.topicDecisions, [i]: dec } }))}
          />
        )}
        {currentStep === 'participants' && (
          <ParticipantFeedbackStep
            participants={participants}
            participantFeedback={formData.participantFeedback}
            onFeedbackChange={(name, fb) => setFormData(prev => ({ ...prev, participantFeedback: { ...prev.participantFeedback, [name]: fb } }))}
          />
        )}
        {currentStep === 'review' && <ReviewStep meeting={meeting} formData={formData} onPublish={publishReport} isSaving={isSaving} />}

        {(() => {
          const steps = Object.keys(STEP_LABELS);
          const currentIdx = steps.indexOf(currentStep);
          const isFirst = currentIdx === 0;
          const isLast = currentIdx === steps.length - 1;
          return (
            <div style={{ display: 'flex', gap: 12, marginTop: 32, justifyContent: 'space-between' }}>
              <button onClick={() => { if (currentIdx > 0) setCurrentStep(steps[currentIdx - 1]); }} style={{ padding: '10px 20px', borderRadius: 6, border: '1.5px solid var(--border)', background: 'transparent', color: 'var(--ink)', fontSize: '0.9rem', fontWeight: 600, cursor: isFirst ? 'default' : 'pointer', opacity: isFirst ? 0 : 1, pointerEvents: isFirst ? 'none' : 'auto', transition: 'var(--transition)', fontFamily: 'var(--font-body)' }}>← Back</button>
              <button onClick={() => { if (isLast) { publishReport(); } else { setCurrentStep(steps[currentIdx + 1]); } }} disabled={isLast && isSaving} style={{ padding: '10px 20px', borderRadius: 6, border: `1.5px solid ${isLast ? 'var(--accent)' : 'var(--navy)'}`, background: isLast ? 'var(--accent)' : 'var(--navy)', color: 'white', fontSize: '0.9rem', fontWeight: 600, cursor: (isLast && isSaving) ? 'not-allowed' : 'pointer', opacity: (isLast && isSaving) ? 0.6 : 1, transition: 'var(--transition)', fontFamily: 'var(--font-body)' }}>
                {isLast ? (isSaving ? '⏳ Publishing...' : '✓ Publish Report') : 'Next →'}
              </button>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
